import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Gavel, Search, RefreshCw } from "lucide-react";
import { getApiUrl } from "@/utils/api";
import PlanningAppealDetailModal from "@/components/shared/PlanningAppealDetailModal";

const API = getApiUrl();

const DECISION_STYLE = {
  Allowed: "bg-emerald-100 text-emerald-800 border-emerald-200",
  Dismissed: "bg-rose-100 text-rose-800 border-rose-200",
  "Split Decision": "bg-amber-100 text-amber-800 border-amber-200",
  Withdrawn: "bg-muted text-muted-foreground border-border",
};

function formatDMY(isoDate) {
  if (!isoDate) return "—";
  const parts = String(isoDate).slice(0, 10).split("-");
  if (parts.length !== 3) return isoDate;
  const [y, m, d] = parts;
  return `${d}/${m}/${y}`;
}

/**
 * Admin -> Reports -> Planning Appeals. Searches our own mirror of the
 * Planning Inspectorate Casework Database (no live API exists upstream —
 * see utils/planning_appeals.py — refreshed weekly via cron).
 */
export default function PlanningAppealsAdmin() {
  const [meta, setMeta] = useState(null);
  const [postcode, setPostcode] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [selectedAppeal, setSelectedAppeal] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/planning-appeals/meta`, { withCredentials: true })
      .then(({ data }) => setMeta(data))
      .catch(() => {});
  }, []);

  const handleSearch = async (e) => {
    e.preventDefault();
    const pc = postcode.trim();
    if (!pc) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/property-admin/planning-appeals/search`, {
        params: { postcode: pc },
        withCredentials: true,
      });
      setResults(data);
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Failed to search planning appeals");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="planning-appeals-admin">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Gavel className="h-6 w-6" /> Planning Appeals
          </h2>
          <p className="text-sm text-muted-foreground mt-1" data-testid="planning-appeals-sync-status">
            {meta?.last_synced_at
              ? `${(meta.total_count || 0).toLocaleString()} appeal records, last synced ${new Date(meta.last_synced_at).toLocaleString("en-GB")}`
              : "Not synced yet — the weekly refresh job populates this automatically."}
          </p>
        </div>
        <form onSubmit={handleSearch} className="flex items-center gap-2">
          <div className="relative">
            <Search className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={postcode}
              onChange={(e) => setPostcode(e.target.value)}
              placeholder="Postcode or street/house name…"
              className="text-sm border border-input rounded px-2 py-1.5 pl-7 bg-background w-56"
              data-testid="planning-appeals-postcode-search"
            />
          </div>
          <button type="submit" className="text-sm px-3 py-1.5 border border-input rounded hover:bg-muted/40" data-testid="planning-appeals-search-btn">
            Search
          </button>
        </form>
      </div>

      {loading && (
        <div className="flex justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {!loading && results && (
        <section className="border border-border rounded bg-background overflow-x-auto" data-testid="planning-appeals-results">
          {results.appeals.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground" data-testid="planning-appeals-empty">
              No planning appeals found near {results.postcode}.
            </div>
          ) : (
            <table className="w-full text-sm min-w-[820px]">
              <thead className="text-left text-muted-foreground text-xs bg-muted/40">
                <tr>
                  <th className="p-3">Case Number</th>
                  <th className="p-3">Site Address</th>
                  <th className="p-3">LPA</th>
                  <th className="p-3">Type</th>
                  <th className="p-3">Decision</th>
                  <th className="p-3">Date</th>
                </tr>
              </thead>
              <tbody>
                {results.appeals.map((a) => (
                  <tr
                    key={a.case_number}
                    onClick={() => setSelectedAppeal(a)}
                    className="border-t border-border hover:bg-muted/20 cursor-pointer"
                    data-testid={`planning-appeal-row-${a.case_number}`}
                  >
                    <td className="p-3 font-mono text-xs">{a.case_number}</td>
                    <td className="p-3 max-w-[260px] truncate" title={a.site_address}>{a.site_address}</td>
                    <td className="p-3">{a.lpa_name}</td>
                    <td className="p-3 text-xs">{a.case_type}</td>
                    <td className="p-3">
                      <span className={`text-xs px-2 py-1 rounded border ${DECISION_STYLE[a.decision] || "bg-muted text-foreground border-border"}`}>
                        {a.decision || "Unknown"}
                      </span>
                    </td>
                    <td className="p-3 whitespace-nowrap text-xs">{formatDMY(a.decision_date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}

      <PlanningAppealDetailModal
        appeal={selectedAppeal}
        open={!!selectedAppeal}
        onOpenChange={(v) => !v && setSelectedAppeal(null)}
      />
    </div>
  );
}
